/* istanbul ignore file */
import * as React from "react";
import "./ProjectItemComponent.less";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { Log, Logger } from "../../services/Logger";
import { Checkbox, DefaultButton, ITag, ProgressIndicator, SearchBox } from "office-ui-fabric-react";
import { NewformaToastIntent } from "../_base/Toast";
import { MailboxItem, OfficeWrapper } from "../../services/OfficeWrapper";
import { SmartFilingManager } from "../../services/SmartFiling/SmartFilingManager";
import { AppPage } from "../shared/navigationHeader/NavigationHeaderComponent";
import { EmailApiService } from "../../services/NewformaApi/EmailApiService";
import { EmailListItem } from "../../models/EmailListResponse";
import { MsGraphApiService } from "../../services/MsGraphApiService";
import { ConfigurationsApiService } from "../../services/NewformaApi/ConfigurationsApiService";
import { OfficeNotificationService } from "../../services/officeUi/OfficeNotificationService";
import { AnalyticsManager } from "../../services/AnalyticsManager";
import { IProjectsService } from "../../services/NewformaApi/IProjectsService";
import { NrnServiceWrapper } from "../../services/NrnServiceWrapper";
import { AuthApiService } from "../../services/NewformaApi/AuthApiService";
import { InvalidateCacheService } from "../../services/NewformaApi/InvalidateCacheService";
import { Messenger } from "../../services/Messenger";
import { OfficeRoamingSettings } from "../../services/OfficeRoamingSettings";
import EmailListComponent from "../shared/emailList/EmailListComponent";
import ItemFieldsComponent from "./ItemFieldsComponent";
import ProjectPickerComponent from "../shared/projectPicker/ProjectPicker";
import { SubmittalsApiService } from "../../services/NewformaApi/SubmittalsApiService";
import { RfiApiService } from "../../services/NewformaApi/RfiApiService";
import { ActionItemApiService } from "../../services/NewformaApi/ActionItemApiService";
import { ProjectSettingsResponse } from "../../models/projects/ProjectSettingsResponse";
import { EmailListItemWithSelectedStatus, ExtendedITag } from "../fileMultipleEmail/FileMultipleEmailComponent";
import { ExpiredSessionError } from "../../models/ExpiredSessionError";
import EmailListRowItemComponent from "../fileMultipleEmail/emailListRowItem/EmailListRowItemComponent";
import LabelComponent from "../shared/label/LabelComponent";
import LinkComponent from "../shared/linkComponent/LinkComponent";
import ProgressComponent from "../shared/progress/ProgressComponent";
import InfiniteScroll from "react-infinite-scroll-component";

interface ProjectItemComponentProps extends LocalizeContextProps {
    logger: Logger;
    onExpiredSession: () => void;
    onShowToast: (message: string | null, type: NewformaToastIntent) => void;
    onSetNavigationPage: (page: AppPage) => void;
    mailboxItem: MailboxItem | null;
    smartFilingManager: SmartFilingManager;
    emailApiService: EmailApiService;
    deleteEmailAfterFiling: boolean;
    msGraphApiService: MsGraphApiService;
    configurationsApiService: ConfigurationsApiService;
    officeNotificationService: OfficeNotificationService;
    analyticsManager: AnalyticsManager;
    projectsService: IProjectsService;
    invalidateCacheService: InvalidateCacheService;
    nrnServiceWrapper: NrnServiceWrapper;
    authApiService: AuthApiService;
    submittalsApiService: SubmittalsApiService;
    rfiApiService: RfiApiService;
    actionItemApiService: ActionItemApiService;
    shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache: boolean;
    theme: string;
    messenger: Messenger;
    officeRoamingSettings: OfficeRoamingSettings;
    onDismissTeachingBubble: () => void;
    showNoProjectsToast: () => void;
    hideNoProjectsToast: () => void;
    culture: string;
    officeWrapper: OfficeWrapper;
    isProjectItemPendingExpectedSupported: boolean;
}

interface ProjectItemComponentState {
    selectedProject: ITag | null;
    selectedItem: ITag | null;
    selectedEmails: EmailListItemWithSelectedStatus[];
    loadingState: Record<string, boolean>;
    isFiling: boolean;
    itemType: string | undefined;
    projectSettings: ProjectSettingsResponse | null;
    loadedEmails: EmailListItem[];
    emails: EmailListItemWithSelectedStatus[];
    failedIds: string[];
    projects: ExtendedITag<string>[];
    shouldUpdateEmails: boolean;
    isLoadingEmails: boolean;
    isLoadingMoreEmails: boolean;
    isLoadingFolderName: boolean;
    emailOffsetToken: string | null;
    searchTerm: string | null;
    folderName: string;
    folderId: string | null;
    currentEmailItem: EmailListItem | null;
}

class ProjectItemComponent extends React.Component<ProjectItemComponentProps, ProjectItemComponentState> {
    componentName = "ProjectItemComponent";

    constructor(props: ProjectItemComponentProps, context: ProjectItemComponentState) {
        super(props, context);
        this.state = {
            selectedProject: null,
            selectedItem: null,
            selectedEmails: [],
            loadingState: {},
            isFiling: false,
            itemType: "",
            projectSettings: null,
            loadedEmails: [],
            emails: [],
            failedIds: [],
            projects: [],
            shouldUpdateEmails: false,
            isLoadingEmails: false,
            isLoadingMoreEmails: false,
            isLoadingFolderName: false,
            emailOffsetToken: null,
            searchTerm: null,
            folderId: null,
            folderName: "",
            currentEmailItem: null,
        };
    }

    private _isMounted = false;

    async componentDidMount(): Promise<void> {
        this._isMounted = true;
        this.props.logger.info(`${this.componentName} mounted`);
        this.props.onSetNavigationPage(AppPage.FileToProjectItem);
        await this.loadOutlookContextAndEmails();
    }

    async componentDidUpdate(
        prevProps: Readonly<ProjectItemComponentProps>,
        prevState: Readonly<ProjectItemComponentState>,
        snapshot?: any
    ): Promise<void> {
        if (this.props.mailboxItem && prevProps.mailboxItem !== this.props.mailboxItem) {
            this.props.logger.info("Mailbox item changed.");
            const [folderId, currentEmailItem] = await Promise.all([
                this.props.msGraphApiService.getCurrentFolderId(),
                this.props.msGraphApiService.getCurrentMessageAsEmailListItem(),
            ]);
            const mappedItems: EmailListItemWithSelectedStatus[] = this.state.loadedEmails.map((item) => ({
                ...item,
                isSelected:
                    item.id === this.state.currentEmailItem?.id
                        ? false
                        : this.state.emails.find((x) => x.id === item.id)?.isSelected || false,
            }));
            const emails = this.moveSelectedEmailToTopOfList(mappedItems, currentEmailItem);
            this.setState({ currentEmailItem, emails });

            if (folderId === this.state.folderId) {
                return;
            }
            const folderName = await this.props.msGraphApiService.getFolderName(folderId);
            this.setState({ emails: [], emailOffsetToken: null, folderId, loadedEmails: [], folderName });
            await this.loadEmails(false, null, null);
        }
    }

    componentWillUnmount(): void {
        this._isMounted = false;
    }

    async loadOutlookContextAndEmails(): Promise<void> {
        await this.loadOutlookContext();
        await this.loadEmails(false, null, null);
    }

    private async onEmailSearch(searchValue: string | null): Promise<void> {
        if (!searchValue) {
            await this.onEmailSearchClear();
            return;
        }

        this.setState({
            searchTerm: searchValue,
            isLoadingEmails: true,
            emailOffsetToken: null,
            emails: [],
            loadedEmails: [],
        });
        await this.loadEmails(false, searchValue, null);
    }

    private async onEmailSearchClear(): Promise<void> {
        this.setState({ emails: [], searchTerm: null, emailOffsetToken: null, loadedEmails: [] });
        await this.loadEmails(false, null, null);
    }

    private async loadEmails(
        isLoadingMore: boolean,
        searchValue: string | null,
        offsetToken: string | null
    ): Promise<void> {
        if (!this._isMounted) {
            this.props.logger.info("loadEmails aborted: component not mounted.");
            return;
        }
        this.setState({ isLoadingEmails: !isLoadingMore, isLoadingMoreEmails: isLoadingMore });
        try {
            if (!this.state.folderId || !this.state.currentEmailItem) {
                throw new Error("missing folderId or currentEmailItem");
            }
            const emailResponse = await this.props.msGraphApiService.getFolderEmails(
                this.state.folderId,
                offsetToken,
                searchValue
            );
            const mappedItems: EmailListItemWithSelectedStatus[] = emailResponse.items.map((item) => ({
                ...item,
                isSelected: false,
            }));

            let emails = [...this.state.emails, ...mappedItems];
            if (!isLoadingMore) {
                emails = this.moveSelectedEmailToTopOfList(emails, this.state.currentEmailItem);
            }
            if (!this._isMounted) {
                return;
            }
            this.setState((state) => ({
                emails,
                loadedEmails: [...state.loadedEmails, ...emailResponse.items],
                emailOffsetToken: emailResponse.paging.offsetToken,
            }));
        } catch (error) {
            this.props.logger.error("FileMultipleEmailComponent - error loading emails");
            this.handleApiError(error, this.props.translate("FILE_MULTIPLE_EMAIL.ERROR_LOADING_EMAILS") as string);
        } finally {
            this.setState({ isLoadingEmails: false, isLoadingMoreEmails: false });
        }
    }

    private moveSelectedEmailToTopOfList(
        emails: EmailListItemWithSelectedStatus[],
        currentlySelectedEmail: EmailListItem
    ): EmailListItemWithSelectedStatus[] {
        const emailsWithoutSelected = emails.filter((email) => email.id !== currentlySelectedEmail.id);
        return [{ ...currentlySelectedEmail, isSelected: true }, ...emailsWithoutSelected];
    }

    private async loadOutlookContext(): Promise<void> {
        this.setState({ isLoadingFolderName: true });
        try {
            const [folderId, currentEmailItem] = await Promise.all([
                this.props.msGraphApiService.getCurrentFolderId(),
                this.props.msGraphApiService.getCurrentMessageAsEmailListItem(),
            ]);
            const folderName = await this.props.msGraphApiService.getFolderName(folderId);
            if (!this._isMounted) {
                return;
            }
            this.setState({ folderName, folderId, currentEmailItem });
        } catch (error) {
            this.props.logger.error("FileMultipleEmailComponent - error loading outlook context");
            this.handleApiError(error, this.props.translate("FILE_MULTIPLE_EMAIL.ERROR_LOADING_EMAILS") as string);
        } finally {
            this.setState({ isLoadingFolderName: false });
        }
    }

    private handleApiError(error: any, messageToDisplay: string): void {
        this.props.logger.error(`FileMultipleEmailComponent API error: ${messageToDisplay}`, error);

        if (ExpiredSessionError.isInstanceOf(error)) {
            this.props.onExpiredSession();
            return;
        }
        this.props.onShowToast(messageToDisplay, "error");
    }

    private isSelectAllIndeterminate(): boolean {
        const selectedEmailIds = this.selectedEmailsIds();
        return selectedEmailIds.length > 0 && selectedEmailIds.length !== this.state.emails.length;
    }

    private areAllItemsSelected(): boolean {
        const selectedEmailIds = this.selectedEmailsIds();
        return selectedEmailIds.length > 0 && selectedEmailIds.length === this.state.emails.length;
    }

    private onSelectAllChanged(event?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void {
        if (checked === undefined) {
            return;
        }

        const emails = this.state.emails;

        const isSelected = checked || (!checked && this.isSelectAllIndeterminate());
        const checkedEmails = emails.map((email) => ({ ...email, isSelected }));
        this.setState({ emails: checkedEmails });
    }

    private onEmailSelectionStateChanged(id: string, checked: boolean): void {
        const emails = this.state.emails;
        const updatedEmails = emails.map((email) => {
            if (email.id === id) {
                return { ...email, isSelected: checked };
            }
            return email;
        });
        this.setState({ emails: updatedEmails });
    }

    private renderEmailRows(): JSX.Element {
        const emailItems = this.state.emails.map((email, index) => {
            return (
                <EmailListRowItemComponent
                    key={`${email.id}${index}`}
                    disabled={this.state.isFiling}
                    subject={email.subject}
                    from={email.from}
                    preview={email.bodyPreview}
                    id={email.id}
                    isFiled={email.isFiled}
                    isSelected={email.isSelected}
                    sentDateTime={email.sentDateTime}
                    onSelectionStateChanged={this.onEmailSelectionStateChanged.bind(this)}
                />
            );
        });

        return <>{emailItems}</>;
    }

    private selectedEmailsIds(): string[] {
        return this.state.emails.filter((email) => email.isSelected).map((email) => email.id);
    }

    private handleLoadingChange = (componentName: string, isLoading: boolean) => {
        this.setState((prev) => ({ loadingState: { ...prev.loadingState, [componentName]: isLoading } }));
    };

    private async getProjectSettings(projectNrn: string) {
        const settings = await this.props.projectsService.getProjectSettings(projectNrn);
        this.setState({ projectSettings: settings ?? null });
    }

    private handleProjectSelected = async (selectedProject: ITag | null) => {
        if (selectedProject?.key) {
            await this.getProjectSettings(selectedProject.key as string);
        }
        // Always clear itemType and selectedItem when project changes
        this.setState({
            selectedProject,
            itemType: undefined,
            selectedItem: null,
        });
    };

    private handleItemTypeChanged = (itemType: string | undefined) => {
        this.setState({ itemType });
    };

    private handleItemSelected = (selectedItem: ITag | null) => {
        this.setState({ selectedItem });
    };

    private updateProjects = (projects: ExtendedITag<string>[]) => {
        this.setState({ projects });
    };

    private onFormSubmit = async () => {
        this.props.logger.info("FileToProject - Form submitted.");
        this.setState({ isFiling: true, shouldUpdateEmails: true });
        const selectedEmailIds = this.selectedEmailsIds();
        // When a conversation gets expanded, the number increases, so save this original value to use in toast
        const numberOfOriginalEmails = selectedEmailIds.length;

        const selectedProject = this.state.selectedProject as ITag;
        const fileConversation = this.props.officeRoamingSettings.getFileEmailConversation();
        if (fileConversation) {
            await this.props.msGraphApiService.expandEmailsForConversationFiling(selectedEmailIds);
        }
        Log.info("FileToProject started");
        const {
            numberOfSuccessfulEmails,
            numberOfFailedEmails,
        } = await this.props.emailApiService.onFileEmailFormSubmit(
            selectedEmailIds,
            selectedProject,
            this.state.projects,
            this.props.culture,
            false, // pass a false because the conversation was already expanded
            this.props.deleteEmailAfterFiling,
            this.state.loadedEmails,
            this.state.selectedItem,
            this.setEmails,
            this.setFailedIds,
            this.props.analyticsManager,
            this.state.emails
        );
        Log.info("FileToProject finished");
        this.setState({
            isFiling: false,
            shouldUpdateEmails: false,
            selectedEmails: this.props.deleteEmailAfterFiling ? this.state.selectedEmails : [],
            selectedItem: this.state.failedIds.length > 1 ? this.state.selectedItem : null,
        });
        this.prepareToast(numberOfOriginalEmails, numberOfSuccessfulEmails, numberOfFailedEmails);
    };

    setEmails = (emails: EmailListItemWithSelectedStatus[], shownEmails?: EmailListItem[]) => {
        this.setState({ emails, loadedEmails: shownEmails ? shownEmails : this.state.loadedEmails });
    };

    setFailedIds = (ids: string[] | undefined) => {
        this.setState({ failedIds: ids || [] });
    };

    private prepareToast(
        numberOfOriginalEmails: number,
        numberOfSuccessfulEmails: number,
        numberOfFailedEmails: number
    ): void {
        const isMulti = numberOfSuccessfulEmails + numberOfFailedEmails > 1;
        const isSuccess = numberOfSuccessfulEmails >= 1 && numberOfFailedEmails === 0;
        const isPartialSuccess = numberOfSuccessfulEmails >= 1 && numberOfFailedEmails >= 1;
        if (isMulti) {
            if (isSuccess) {
                const toast = String(this.props.translate("PROJECT_ITEM.TOAST_MULTI_SUCCESS")).replace(
                    "[[count]]",
                    String(numberOfOriginalEmails)
                );
                this.props.onShowToast(toast, "success");
            } else if (isPartialSuccess) {
                const toast = String(this.props.translate("PROJECT_ITEM.TOAST_MULTI_PARTIAL_SUCCESS"))
                    .replace("[[countfailed]]", String(numberOfFailedEmails))
                    .replace("[[countsuccess]]", String(numberOfSuccessfulEmails));
                this.props.onShowToast(toast, "warning");
            } else {
                const toast = String(this.props.translate("PROJECT_ITEM.TOAST_MULTI_FAILURE")).replace(
                    "[[count]]",
                    String(numberOfFailedEmails)
                );
                this.props.onShowToast(toast, "error");
            }
        } else {
            if (isSuccess) {
                const toast = String(this.props.translate("PROJECT_ITEM.TOAST_SINGLE_SUCCESS"));
                this.props.onShowToast(toast, "success");
            } else {
                const toast = String(this.props.translate("PROJECT_ITEM.TOAST_SINGLE_FAILURE"));
                this.props.onShowToast(toast, "error");
            }
        }
    }

    private get isLoading() {
        return Object.values(this.state.loadingState).some(Boolean);
    }

    private isFormValid(): boolean {
        return !!this.selectedEmailsIds().length && !!this.state.selectedProject && !!this.state.selectedItem;
    }

    private getFilingMessage(): string {
        return (this.props.translate("FILE_MULTIPLE_EMAIL.FILING_IN_PROGRESS") as string).replace(
            `[[count]]`,
            `${this.selectedEmailsIds().length}`
        );
    }

    private getButtonText(): string {
        const count = this.selectedEmailsIds().length ? `(${this.selectedEmailsIds().length})` : "";
        return `${(this.props.translate("PROJECT_ITEM.SUBMIT_BUTTON") as string).toLocaleUpperCase()} ${count}`.trim();
    }

    private getLoadingIndicator(): JSX.Element {
        return (
            <>
                {this.state.isLoadingMoreEmails || this.state.isLoadingEmails || this.state.isLoadingFolderName ? (
                    <ProgressComponent
                        className="newforma-loadingEmailsIndicator"
                        message={this.props.translate("FILE_MULTIPLE_EMAIL.LOADING_EMAILS") as string}
                    />
                ) : null}
            </>
        );
    }

    render(): JSX.Element {
        return (
            <>
                <div className="newforma-projectItemComponent">
                    <div className="newforma-projectItemComponentForm">
                        <ProjectPickerComponent
                            allowGlobalProjects={false}
                            logger={this.props.logger}
                            onProjectSelected={this.handleProjectSelected}
                            smartFilingManager={this.props.smartFilingManager}
                            mailboxItem={this.props.mailboxItem}
                            theme={this.props.theme}
                            culture={this.props.culture}
                            onExpiredSession={this.props.onExpiredSession}
                            onShowToast={this.props.onShowToast}
                            projectsService={this.props.projectsService}
                            invalidateCacheService={this.props.invalidateCacheService}
                            shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache={
                                this.props.shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache
                            }
                            messenger={this.props.messenger}
                            officeRoamingSettings={this.props.officeRoamingSettings}
                            showNoProjectsToast={this.props.showNoProjectsToast}
                            hideNoProjectsToast={this.props.hideNoProjectsToast}
                            isFiling={this.state.isFiling}
                            onLoadingChange={this.handleLoadingChange}
                            updateProjects={this.updateProjects}
                            allowProjectDisabled={true}
                            shouldParseEmailSubject={true}
                        />
                        <ItemFieldsComponent
                            logger={this.props.logger}
                            selectedProject={this.state.selectedProject}
                            selectedItem={this.state.selectedItem}
                            isDisabled={this.state.isFiling || !this.state.selectedProject}
                            theme={this.props.theme}
                            onItemSelectionChange={this.handleItemSelected} // binding is not required if the handler is an arrow function
                            onItemTypeChanged={this.handleItemTypeChanged}
                            onLoadingChange={this.handleLoadingChange}
                            submittalsApiService={this.props.submittalsApiService}
                            rfisApiService={this.props.rfiApiService}
                            actionItemApiService={this.props.actionItemApiService}
                            projectSettings={this.state.projectSettings}
                            officeWrapper={this.props.officeWrapper}
                            mailboxItem={this.props.mailboxItem}
                            isProjectItemPendingExpectedSupported={this.props.isProjectItemPendingExpectedSupported}
                            projects={this.state.projects}
                        />

                        <div className={`newforma-inputLabelContainer`}>
                            <LabelComponent
                                className="searchBoxLabel"
                                text={this.props.translate("FILE_MULTIPLE_EMAIL.SEARCH_BOX_LABEL") as string}
                            />
                            <LinkComponent
                                text={this.props.translate("SHARED.REFRESH_EMAILS") as string}
                                icon="Refresh"
                                onClick={() => this.onEmailSearchClear()}
                            />
                        </div>
                        <SearchBox
                            className="newforma-searchBox"
                            placeholder={this.props.translate("FILE_MULTIPLE_EMAIL.SEARCH_BOX_PLACEHOLDER") as string}
                            onSearch={this.onEmailSearch.bind(this)}
                            onClear={this.onEmailSearchClear.bind(this)}
                        />
                        <div
                            className={`newforma-multipleEmailListHeader ${
                                this.state.isLoadingFolderName ? "hidden" : ""
                            }`}
                        >
                            <div className="newforma-selectEmailHeader">
                                <Checkbox
                                    id="selectAll-checkbox"
                                    className="newforma-checkbox"
                                    checked={this.areAllItemsSelected()}
                                    indeterminate={this.isSelectAllIndeterminate()}
                                    onChange={this.onSelectAllChanged.bind(this)}
                                    disabled={this.state.isLoadingEmails || this.state.isFiling}
                                />
                                <LabelComponent
                                    className="emailLabel"
                                    text={this.props.translate("FILE_MULTIPLE_EMAIL.SELECT_EMAIL") as string}
                                    required={true}
                                />
                            </div>
                            <LabelComponent
                                className="newforma-folderLabel"
                                text={this.state.folderName}
                                showTooltip={true}
                            />
                        </div>
                        <div id="scrollableEmailList" className="newforma-scrollableEmailList">
                            {this.state.isLoadingEmails || this.state.isLoadingFolderName ? (
                                this.getLoadingIndicator()
                            ) : (
                                <InfiniteScroll
                                    className="newforma-infiniteScroll"
                                    scrollableTarget="scrollableEmailList"
                                    dataLength={this.state.emails.length}
                                    next={this.loadEmails.bind(
                                        this,
                                        true,
                                        this.state.searchTerm,
                                        this.state.emailOffsetToken
                                    )}
                                    hasMore={!!this.state.emails.length && !!this.state.emailOffsetToken}
                                    loader={this.getLoadingIndicator()}
                                    endMessage={
                                        <div className="newforma-endOfListMessage">
                                            {this.props.translate("FILE_MULTIPLE_EMAIL.END_OF_LIST") as string}
                                        </div>
                                    }
                                >
                                    {this.renderEmailRows()}
                                </InfiniteScroll>
                            )}
                        </div>
                    </div>
                    {this.state.isFiling && (
                        <ProgressIndicator
                            label={this.getFilingMessage()}
                            className="newforma-progressIndicator"
                            styles={{
                                itemName: ".ms-label",
                            }}
                        />
                    )}
                </div>
                <div id="footer" className="newforma-footer">
                    <DefaultButton
                        className="newforma-footerButton"
                        id="fileProjectItemButton"
                        primary={true}
                        onClick={this.onFormSubmit}
                        text={this.getButtonText()}
                        disabled={this.state.isFiling || !this.isFormValid() || this.isLoading}
                    />
                </div>
            </>
        );
    }
}

export default withLocalize(ProjectItemComponent);
